import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Dictionary, Locale } from './types';
import en from './locales/en';
import fr from './locales/fr';
import ar from './locales/ar';

const STORAGE_KEY = 'codex-locale';
const DICTS: Record<Locale, Dictionary> = { en, fr, ar };
export const LOCALES: Locale[] = ['fr', 'en', 'ar'];

type I18nContextValue = {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  t: Dictionary;
  setLocale: (locale: Locale) => void;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function isLocale(v: string | null): v is Locale {
  return v === 'en' || v === 'fr' || v === 'ar';
}

function detectLocale(): Locale {
  if (typeof window === 'undefined') return 'fr';
  const pathLoc = window.location.pathname.split('/').filter(Boolean)[0];
  if (isLocale(pathLoc)) return pathLoc;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    /* ignore */
  }
  const nav = navigator.language.toLowerCase();
  if (nav.startsWith('ar')) return 'ar';
  if (nav.startsWith('en')) return 'en';
  return 'fr';
}

let arabicFontsLoaded = false;

function applyLocale(locale: Locale) {
  const root = document.documentElement;
  const lang = locale === 'ar' ? 'ar' : locale;
  const dir = locale === 'ar' ? 'rtl' : 'ltr';
  if (root.lang !== lang) root.lang = lang;
  if (root.dir !== dir) root.dir = dir;
  if (root.dataset.locale !== locale) root.dataset.locale = locale;
  if (locale === 'ar' && !arabicFontsLoaded) {
    arabicFontsLoaded = true;
    void import('../../styles/fonts-arabic.css');
  }
}

export function getInitialLocale(): Locale {
  return detectLocale();
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  useEffect(() => {
    applyLocale(locale);
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      dir: locale === 'ar' ? 'rtl' : 'ltr',
      t: DICTS[locale],
      setLocale,
    }),
    [locale, setLocale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export type { Dictionary, Locale };
